/**
 * Collection filtering, sorting, density and load-more (F2, F3, F4).
 *
 * Everything routes through one idea: build a URL, ask the server to re-render
 * this section, swap the result in. Result counts, facet counts and the
 * disabled state of zero-result values all come from the server, because
 * recomputing them client-side drifts from what a reload would show.
 *
 * Progressive enhancement: the facet form is a real GET form and the
 * pagination links are real links. With JS off, both still work — you just
 * get a full page load.
 */
(function () {
  'use strict';

  var V = window.Velocity;
  if (!V) return;

  var DENSITY_KEY = 'velocity:grid-density';

  class CollectionFacets extends V.Element {
    init() {
      this.sectionId = this.dataset.sectionId;
      this.bind();
      this.initDensity();

      // Back/forward through filter states must re-render the grid.
      this.on(window, 'popstate', this.onPopState.bind(this));
    }

    /** Re-bound after every swap, since the previous nodes are gone. */
    bind() {
      var self = this;

      this.addEventListener('change', function (e) {
        if (e.target.closest('[data-facet-form]')) {
          self.applyFilters();
        } else if (e.target.closest('[data-sort-select]')) {
          self.applySort(e.target.value);
        }
      });

      // Debounce price inputs — typing "150" should not fire three requests.
      this.addEventListener('input', V.debounce(function (e) {
        if (e.target.closest('.facet__price')) self.applyFilters();
      }, 500));

      this.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-facet-remove], [data-facet-clear]');
        if (chip) {
          e.preventDefault();
          self.navigate(chip.getAttribute('href'));
          return;
        }

        var loadMore = e.target.closest('[data-load-more]');
        if (loadMore) {
          e.preventDefault();
          self.loadMore(loadMore);
          return;
        }

        var pageLink = e.target.closest('.pagination__link');
        if (pageLink && pageLink.getAttribute('href')) {
          e.preventDefault();
          self.navigate(pageLink.getAttribute('href'), { scrollToTop: true });
        }
      });
    }

    /** Serialise the facet form into a URL and go. */
    applyFilters() {
      var form = this.querySelector('[data-facet-form]');
      if (!form) return;

      var params = new URLSearchParams(new FormData(form));

      // Drop empty values so the URL stays readable and shareable.
      var cleaned = new URLSearchParams();
      params.forEach(function (value, key) {
        if (value !== '') cleaned.append(key, value);
      });

      var base = window.location.pathname;
      this.navigate(base + (cleaned.toString() ? '?' + cleaned.toString() : ''));
    }

    applySort(value) {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');     // sorting resets to page 1
      this.navigate(url.pathname + url.search);
    }

    /**
     * Fetch the section for `url`, swap it in, and push the URL so the state
     * is shareable and the back button works.
     */
    navigate(url, options) {
      var self = this;
      var opts = options || {};

      this.setBusy(true);

      return V.fetchSection(this.sectionId, url)
        .then(function (fragment) {
          var next = fragment.querySelector('[data-section-id]');
          if (!next) return;

          self.swap(next);

          if (!opts.fromHistory) {
            window.history.pushState({ facets: true }, '', url);
          }
          if (opts.scrollToTop) {
            self.scrollIntoView({ behavior: V.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
          }
        })
        .catch(function () {
          // A failed fetch should not leave the shopper on a stale grid with
          // no way forward — fall back to a real navigation.
          window.location.href = url;
        })
        .then(function () {
          self.setBusy(false);
        });
    }

    swap(next) {
      // Keep the filter drawer open across a swap on mobile, otherwise the
      // shopper is thrown out of the panel after every checkbox.
      var openFacets = this.querySelector('.facets.is-open');
      this.innerHTML = next.innerHTML;

      if (openFacets) {
        var facets = this.querySelector('.facets');
        if (facets) {
          facets.hidden = false;
          facets.classList.add('is-open');
        }
      }

      this.applyDensity(this.density);
      if (V.motion) V.motion.refresh(this);
    }

    onPopState() {
      this.navigate(window.location.pathname + window.location.search, { fromHistory: true });
    }

    setBusy(isBusy) {
      this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    /**
     * F4 — append the next page rather than replacing the grid, and update the
     * button to point at the page after that. Real pagination links stay in
     * the DOM, so crawlable URLs are never removed.
     */
    loadMore(button) {
      var self = this;
      var url = button.dataset.nextUrl;
      if (!url) return;

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      V.fetchSection(this.sectionId, url)
        .then(function (fragment) {
          var grid = self.querySelector('[data-product-grid]');
          var nextGrid = fragment.querySelector('[data-product-grid]');
          if (!grid || !nextGrid) return;

          var added = Array.prototype.slice.call(nextGrid.children);
          added.forEach(function (item) { grid.appendChild(item); });

          // Point the button at the following page, or retire it.
          var nextButton = fragment.querySelector('[data-load-more]');
          if (nextButton && nextButton.dataset.nextUrl) {
            button.dataset.nextUrl = nextButton.dataset.nextUrl;
          } else {
            button.remove();
          }

          // Keep the crawlable pagination in step with what is shown.
          var pagination = self.querySelector('[data-pagination]');
          var nextPagination = fragment.querySelector('[data-pagination]');
          if (pagination && nextPagination) pagination.innerHTML = nextPagination.innerHTML;

          // Reflect the appended page in the URL without a scroll jump, so a
          // reload or a shared link lands on what the shopper was looking at.
          window.history.replaceState({ facets: true }, '', url);

          if (V.motion) V.motion.refresh(grid);

          // Move focus to the first newly added card for keyboard users.
          var firstNew = added[0] && added[0].querySelector('a[href]');
          if (firstNew) firstNew.focus({ preventScroll: true });
        })
        .catch(function () {
          window.location.href = url;
        })
        .then(function () {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        });
    }

    /* --------------------------------------------------------------------
     * F2 — grid density, persisted per browser.
     * ------------------------------------------------------------------ */
    initDensity() {
      var stored = null;
      try {
        stored = localStorage.getItem(DENSITY_KEY);
      } catch (e) { /* private mode */ }

      this.density = stored || null;
      if (this.density) this.applyDensity(this.density);
      else this.syncDensityButtons();

      var self = this;
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-density]');
        if (!btn) return;
        self.density = btn.dataset.density;
        try {
          localStorage.setItem(DENSITY_KEY, self.density);
        } catch (err) { /* not persisted, still applied */ }
        self.applyDensity(self.density);
      });
    }

    applyDensity(density) {
      var grid = this.querySelector('[data-product-grid]');
      if (grid && density) grid.style.setProperty('--grid-cols-desktop', density);
      this.syncDensityButtons();
    }

    syncDensityButtons() {
      var current = this.density;
      Array.prototype.forEach.call(this.querySelectorAll('[data-density]'), function (btn) {
        var isActive = btn.dataset.density === current;
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.classList.toggle('is-active', isActive);
      });
    }
  }

  V.define('collection-facets', CollectionFacets);
})();
